/**
 * The MajiTap mark (ADR 0023): the organic drop with the tap, from the design file (`mAfwK`,
 * Logo / Mark / Drop Tap / Organic), exported with pen.dev and composed into one 400-unit square.
 * Each layer keeps the design's fill. The outline (drop, tap and drip) is `--logo-outline`:
 * navy on light, pale sky on the Maji dark theme. No React and no CSS here, so a Vite config and
 * the splash HTML can use it too.
 */

/** The square the mark sits in, trimmed to the drop and its outline. */
export const MARK_VIEWBOX = "4 4 392 392";

/** The outline's width in the mark's units (12 of 400, as the design). */
export const MARK_OUTLINE_WIDTH = 12;

export interface MarkLayer {
  fill: string;
  /** Drawn again as an outline on top of its fill. */
  outline: boolean;
  transform: string;
  d: string;
}

/** Back to front: the drop, the wave, the water, the highlight, the tap, its handle, the drip. */
export const MARK_LAYERS: MarkLayer[] = [
  {
    fill: "#6ECFF2",
    outline: true,
    transform: "translate(64.2 16.79)",
    d: "M271.88 233.93c0 74.03-50.96 132.89-133.28 132.89-85.57 0-138.6-60.82-138.6-132.89 0-74.26 50.12-141.9 92.29-181.61 24.19-22.34 55.05-43.68 68.66-49.5 11.42-4.59 21.67-3.19 23.91 1.51 1.57 3.3-0.22 6.27-3.58 7.39-7.9 2.52-12.21 10.14-12.21 19.99 0 17.86 13.1 32.42 28.95 49.67 33.77 36.34 73.86 86.46 73.86 152.54z",
  },
  {
    fill: "#3DB6EA",
    outline: false,
    transform: "translate(72.44 248.308) scale(0.959 0.964)",
    d: "M267.9 20.16c-16.58 16.58-41.72 27.44-65.24 27.44-25.37 0-47.82-11.59-66.64-23.63-22.9-14.67-48.5-23.97-71.51-23.97-28.56 0-51.52 11.82-64.51 26.43 1.29 9.52 3.64 17.92 6.78 25.48 15.79-11.54 36.18-18.48 56.84-18.48 26.43 0 50.12 10.19 70.45 21.9 21.06 12.1 44.97 20.38 65.63 20.38 28.62 0 52.3-13.83 63.17-32.14 2.52-7.45 4.14-15.62 5.04-23.41z",
  },
  {
    fill: "#0779C8",
    outline: false,
    transform: "translate(78.938 280.399) scale(0.958 0.962)",
    d: "M192.92 42.28c-20.66 0-44.58-8.29-65.63-20.38-20.33-11.7-44.02-21.9-70.45-21.9-21.78 0-42.28 7.45-56.84 18.48 17.36 49.95 65.86 83.38 129.58 83.38 67.76 0 113.79-40.54 126.28-91.56-12.99 18.82-37.07 31.98-62.94 31.98z",
  },
  {
    fill: "#FFFFFFCC",
    outline: false,
    transform: "translate(93.488 67.967)",
    d: "M85.06 0.47c0.39-0.39-0.11-0.67-1.06-0.28-14.06 8.01-36.85 28.45-54.15 53.87-18.14 26.6-29.29 57.74-29.85 76.55 0 0.56 0.62 0.67 0.9 0.17 11.93-26.54 24.81-50.23 39.65-71.85 14.28-20.89 31.92-43.18 44.52-58.46z",
  },
  {
    fill: "#FFFFFF",
    outline: true,
    transform: "translate(157.384 162.18)",
    d: "M64.74 15.51 L56.06 15.51 C52.36 15.51 48.83 15.06 46.26 14.11 L46.26 0 L28.5 0 L28.5 14.22 C27.72 14.5 18.9 14.76 17.62 14.82 C2.33 15.49 0 15.32 0 34.16 C0 43.23 3.12 46.32 16.62 46.32 C22.89 46.32 32.09 47.04 36.79 44.97 C42.56 42.39 48.44 40.6 56.73 40.6 L64.96 40.6 C75.77 40.6 82.43 48.05 83.16 57.23 C83.55 62.66 89.54 66.08 95.31 66.08 C102.54 66.08 108.08 62.1 107.8 56.5 C106.85 32.87 87.3 15.51 64.74 15.51 L64.74 15.51 Z",
  },
  {
    fill: "#FFFFFF",
    outline: true,
    transform: "translate(162.032 144.036)",
    d: "M57.4 0l-48.05 0c-5.38 0-9.35 3.98-9.35 8.96 0 5.21 4.09 9.18 9.35 9.18l48.05 0c5.32 0 9.41-3.98 9.41-9.18 0-4.98-4.09-8.96-9.41-8.96z",
  },
  {
    fill: "#0779C8",
    outline: true,
    transform: "translate(239.648 232.74)",
    d: "M13.22 0c-5.94 11.31-13.22 23.69-13.22 31.47 0 8.46 6.16 13.72 13.22 13.72 7.67 0 13.78-5.38 13.78-13.72 0-8.46-8.46-21.95-13.78-31.47z",
  },
];

/** The mark as SVG markup. `outline` is a colour or a CSS var(); the default suits light backgrounds. */
export function markSvg({
  outline = "var(--logo-outline, #032554)",
  attrs = "",
}: {
  outline?: string;
  attrs?: string;
} = {}) {
  const layers = MARK_LAYERS.map((l) => {
    const fill = `<path fill="${l.fill}" d="${l.d}"/>`;
    const line = l.outline
      ? `<path fill="none" stroke="${outline}" stroke-width="${MARK_OUTLINE_WIDTH}" stroke-linejoin="round" d="${l.d}"/>`
      : "";
    return `<g transform="${l.transform}">${fill}${line}</g>`;
  }).join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${MARK_VIEWBOX}"${attrs ? ` ${attrs}` : ""}>${layers}</svg>`;
}
